import styles from './home-page.module.scss';
import React from "react";
import { Link } from "react-router-dom";

import Hero from "../../components/hero/hero";
import type { HeroSlide } from "../../components/hero/hero";
import { HERO_WAVE_HOME } from "../../components/hero/waves";
import CategoryCard from "../../components/category-card/category-card";
import { recipes as staticRecipes } from "../../data/recipes";
import site from "../../data/site";
import api from "../../services/api";
import type { Category, Content, Recipe } from "../../services/api";

export interface HomePageProps {
  name?: string;
  classes?: string;
}

/** Fallback del strip mientras carga / si el API falla (assets locales). */
const DEFAULT_STRIP = [
  "manchego",
  "oaxaca",
  "gouda",
  "panela",
  "provolone",
  "yogurt",
].map((n) => `/assets/productos/${n}.jpg`);

/** Fallback de la Bienvenida (mientras carga / si el API falla). */
const DEFAULT_WELCOME = {
  label: "Bienvenida",
  content:
    "MangiBone es una empresa fundada en el año 2008 con el liderazgo de la Ing. Cynthia M. Rodríguez y su familia quienes con mucho esfuerzo, cariño y dedicación han buscado satisfacer las necesidades de sus clientes ofreciendo productos de gran calidad.",
  excerpt:
    "MangiBone se crea con el objetivo primordial de cuidar la salud de sus consumidores ofreciendo alternativas de consumo de alto valor nutricional de excelente sabor, textura y viscosidad. Todos nuestros productos MangiBone los hacemos con amor.",
};

/** Fallback del mapa (mientras carga / si el API falla). */
const DEFAULT_MAP = {
  label: "Visítanos",
  content:
    '<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d449.3141394367877!2d-98.36906093297358!3d20.08575801551219!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x85d05658b4755887%3A0xc707411911260f8a!2sEl%20Queco!5e0!3m2!1ses!2smx!4v1792011514956!5m2!1ses!2smx" width="100%" height="100%" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>',
};

interface State {
  /** Slides del hero (bloques editoriales `category=hero`). */
  heroSlides?: HeroSlide[];
  /** Imágenes del carrusel de productos (`/api/products`). */
  strip?: string[];
  /** Tarjetas de categoría (`/api/categories`). */
  categories?: Category[];
  /** Bloque de bienvenida (`/api/contents?filters.category=welcome`). */
  welcome?: Content;
  /** Bloque de mapa (`/api/contents?filters.category=map`). */
  map?: Content;
  /** Recetas (`/api/recipes`) para la sección "Nuestro recetario". */
  recipes?: Recipe[];
}

/**
 * Inicio (Figma `110:400`, 1920×5296).
 * Secciones: hero curvo (logo + slogan), nuestros productos (carrusel de fotos +
 * categorías), bienvenida (banda azul marino + tienda), nuestro recetario y
 * barra degradada. El footer vive en el layout raíz.
 *
 * Consume el API público con `site.*`/locales como fallback:
 * - hero  → `/api/contents?filters.category=hero`
 * - strip → `/api/products`
 * - cards → `/api/categories`
 */
export default class HomePage extends React.Component<HomePageProps, State> {
  static jsClass = "HomePage";
  static defaultProps: Partial<HomePageProps> = {
    name: "mbHome",
  };

  state: State = {};

  /** Id único del `clipPath` circular de la Bienvenida. */
  private storeClipId = "mbWelcomeCircle-" + Math.random().toString(36).slice(2, 8);

  componentDidMount() {
    api
      .listContents("hero")
      .then((contents) => {
        const heroSlides = contents.length
          ? contents.map((c) => ({
              src: c.background || site.heroImage,
              logo: site.brand.logo,
              slogan: c.label || site.brand.slogan,
              time: c.time,
            }))
          : undefined;
        this.setState({ heroSlides });
      })
      .catch(() => this.setState({ heroSlides: undefined }));

    api
      .listProducts()
      .then((products) => {
        const strip = products.map((p) => p.imagen).filter(Boolean);
        this.setState({ strip: strip.length ? strip : undefined });
      })
      .catch(() => this.setState({ strip: undefined }));

    api
      .listCategories()
      .then((categories) => this.setState({ categories }))
      .catch(() => this.setState({ categories: undefined }));

    api
      .listContents("welcome")
      .then((contents) => this.setState({ welcome: contents[0] }))
      .catch(() => this.setState({ welcome: undefined }));

    api
      .listContents("map")
      .then((contents) => this.setState({ map: contents[0] }))
      .catch(() => this.setState({ map: undefined }));

    api
      .listRecipes()
      .then((recipes) => this.setState({ recipes }))
      .catch(() => this.setState({ recipes: undefined }));
  }

  render(): React.ReactNode {
    const { classes } = this.props;
    const { heroSlides, strip, categories, welcome, map, recipes } = this.state;
    const featured = (recipes ?? staticRecipes).slice(0, 3);
    const stripImages = strip ?? DEFAULT_STRIP;
    const categoryCards = categories ?? site.categories;
    const welcomeTitle = welcome?.label || DEFAULT_WELCOME.label;
    const welcomeP1 = welcome?.content || DEFAULT_WELCOME.content;
    const welcomeP2 = welcome?.excerpt || DEFAULT_WELCOME.excerpt;
    const welcomeImage = welcome?.background || site.welcomeImage;
    const mapTitle = map?.label || DEFAULT_MAP.label;
    const mapHtml = map?.content || DEFAULT_MAP.content;

    return (
      <div className={styles['mb-home'] + (classes ? ' ' + classes : '')}>
        <Hero
          slides={heroSlides}
          src={site.heroImage}
          logo={site.brand.logo}
          slogan={site.brand.slogan}
          wave={HERO_WAVE_HOME}
        />

        {/* ── Bienvenida ─────────────────────────────────────────────────── */}
        <section className={styles['mb-welcome']}>
          <div className={styles['mb-wrap']}>
            <h2 className={styles['mb-h2']}>{welcomeTitle}</h2>
          </div>
          <div className={styles['mb-welcome__band']}>
            <div className={styles['mb-wrap']}>
              <div className={styles['mb-welcome__grid']}>
                <p className={styles['mb-welcome__copy']}>{welcomeP1}</p>
                <p className={styles['mb-welcome__copy']}>{welcomeP2}</p>
                <div className={styles['mb-welcome__store']}>
                  <svg
                    className={styles['mb-welcome__store-svg']}
                    viewBox="0 0 100 100"
                    role="img"
                    aria-label="Cremería MangiBone">
                    <defs>
                      <clipPath id={this.storeClipId}>
                        <circle cx="50" cy="50" r="50" />
                      </clipPath>
                    </defs>
                    <image
                      href={welcomeImage}
                      x="0"
                      y="0"
                      width="100"
                      height="100"
                      preserveAspectRatio="xMidYMid slice"
                      clipPath={`url(#${this.storeClipId})`}
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Nuestros productos ─────────────────────────────────────────── */}
        <section className={styles['mb-products']} id="productos">
          <div className={styles['mb-wrap']}>
            <h2 className={styles['mb-h2'] + ' ' + styles['mb-h2--center']}>Nuestros productos</h2>
          </div>
          <div className={styles['mb-products__strip']} aria-hidden="true">
            <div className={styles['mb-products__track']}>
              {[...stripImages, ...stripImages].map((src, i) => (
                <img key={`${i}-${src}`} src={src} alt="" loading="lazy" />
              ))}
            </div>
          </div>
          <div className={styles['mb-wrap']}>
            <div className={styles['mb-products__cards']}>
              {categoryCards.map((category) => (
                <CategoryCard key={category.title} category={category} to={category.link} />
              ))}
            </div>
          </div>
        </section>

        {/* ── Mapa / ubicación ───────────────────────────────────────────── */}
        <section className={styles['mb-map']} id="ubicacion">
          <div className={styles['mb-wrap']}>
            <h2 className={styles['mb-h2'] + ' ' + styles['mb-h2--center']}>{mapTitle}</h2>
          </div>
          <div
            className={styles['mb-map__frame']}
            dangerouslySetInnerHTML={{ __html: mapHtml }}
          />
        </section>

        {/* ── Nuestro recetario ──────────────────────────────────────────── */}
        <section className={styles['mb-recipes']} id="recetario">
          <div className={styles['mb-wrap']}>
            <h2 className={styles['mb-h2'] + ' ' + styles['mb-h2--center']}>Nuestro recetario</h2>
            <div className={styles['mb-recipes__grid']}>
              {featured.map((recipe) => (
                <Link
                  className={styles['mb-recipes__item']}
                  key={recipe.slug}
                  to={`/recetario/${recipe.slug}`}>
                  <img
                    className={styles['mb-recipes__img']}
                    src={recipe.imagen}
                    alt={recipe.titulo}
                    loading="lazy"
                  />
                  <h3 className={styles['mb-recipes__title']}>{recipe.titulo}</h3>
                  <p className={styles['mb-recipes__copy']}>{recipe.descripcion}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }
}
